import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Car,
  Shield,
  Phone,
  MapPin,
  Clock,
  Menu,
  X,
  ChevronRight,
  Wrench,
  Sparkles,
  Award,
  Users,
  Zap,
  CheckCircle,
  MessageCircle,
  Send,
  Star,
  FileCheck,
  SquareStack,
  CircleDot
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Gallery", href: "#gallery" },
    { name: "Contact", href: "#contact" }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white header-scrolled" : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => scrollToSection("#home")}>
            <div className="w-10 h-10 bg-[#0B3D91] rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-sm md:text-base font-bold text-[#111111] leading-tight">
                Car Glass & Body Parts
              </h1>
              <p className="text-xs text-[#0B3D91] font-medium">Specialist</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="nav-link text-[#111111] font-medium text-sm hover:text-[#0B3D91] transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="flex items-center gap-3">
            <a
              href="tel:9511323693"
              className="hidden md:flex items-center gap-2 bg-[#E53935] hover:bg-[#C62828] text-white px-4 py-2 rounded-md font-semibold text-sm btn-accent"
              data-testid="header-call-btn"
            >
              <Phone className="w-4 h-4" />
              <span>9511323693</span>
            </a>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-[#111111]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-white border-t border-gray-100 py-4"
          >
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="block w-full text-left px-4 py-3 text-[#111111] font-medium hover:bg-gray-50"
              >
                {link.name}
              </button>
            ))}
            <a
              href="tel:9511323693"
              className="flex items-center gap-2 mx-4 mt-3 bg-[#E53935] text-white px-4 py-3 rounded-md font-semibold justify-center"
            >
              <Phone className="w-4 h-4" />
              <span>Call Now: 9511323693</span>
            </a>
          </motion.div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20"
      style={{
        backgroundImage: `url('https://images.pexels.com/photos/9661334/pexels-photo-9661334.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div className="absolute inset-0 hero-overlay"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
        <div className="max-w-3xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-[#E53935]"></div>
              <span className="text-[#E53935] font-semibold text-sm uppercase tracking-wider">
                15+ Years of Excellence
              </span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight"
            >
              Car Glass & Body Parts{" "}
              <span className="text-[#E53935]">Specialist</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-lg sm:text-xl text-gray-200 mb-4"
            >
              Expert Car Glass Repair & Replacement in Jaipur
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-wrap items-center gap-4 text-gray-300 mb-8"
            >
              <span className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#E53935]" />
                Fast
              </span>
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#E53935]" />
                Reliable
              </span>
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[#E53935]" />
                Affordable
              </span>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <a
                href="tel:9511323693"
                className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#C62828] text-white px-6 py-3 rounded-md font-semibold text-base btn-accent"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-[#0B3D91] px-6 py-3 rounded-md font-semibold text-base btn-primary"
                data-testid="hero-quote-btn"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Get Quote
                <ChevronRight className="w-5 h-5" />
              </a>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex items-center gap-2 mt-8 text-gray-300"
            >
              <MapPin className="w-4 h-4" />
              <span className="text-sm">Nirman Nagar, Jaipur, Rajasthan</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1619642737579-a7474bee1044?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Professional mechanic at work"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B3D91]/90 to-transparent p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-extrabold text-[#0B3D91]">15+</span>
                  </div>
                  <div className="text-white">
                    <p className="font-bold text-lg">Years of</p>
                    <p className="text-gray-200">Trusted Service</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#E53935]/10 rounded-2xl -z-10"></div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-12 h-1 bg-[#0B3D91]"></div>
              <span className="text-[#0B3D91] font-semibold text-sm uppercase tracking-wider">
                About Us
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-[#111111] mb-6">
              Your Trusted Partner for{" "}
              <span className="text-[#0B3D91]">Car Glass Solutions</span>
            </h2>

            <p className="text-gray-600 mb-6 leading-relaxed">
              Car Glass & Body Parts Specialist is a trusted name in Jaipur with over 15 years 
              of experience in car glass repair, replacement, and body parts solutions. We provide 
              high-quality service using professional tools and genuine parts.
            </p>

            <p className="text-gray-600 mb-8 leading-relaxed">
              Our goal is to provide fast, affordable, and reliable service to all customers. 
              Whether you need a windshield replacement or minor glass repair, our expert 
              technicians ensure quality workmanship every time.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Shield, text: "Genuine Parts" },
                { icon: Award, text: "Expert Technicians" },
                { icon: Zap, text: "Fast Service" },
                { icon: Users, text: "Customer Focused" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-[#0B3D91]/10 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-[#0B3D91]" />
                  </div>
                  <span className="font-medium text-[#111111]">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    { icon: SquareStack, title: "Car Glass Replacement", desc: "Complete glass replacement for all car models" },
    { icon: Wrench, title: "Car Glass Repair", desc: "Expert repair for chips and cracks" },
    { icon: Shield, title: "Windshield Repair", desc: "Professional windshield restoration" },
    { icon: Car, title: "Car Body Parts", desc: "Genuine body parts and accessories" },
    { icon: CircleDot, title: "Door Glass Replacement", desc: "Side door glass installation" },
    { icon: Sparkles, title: "Side Mirror Replacement", desc: "Mirror repair and replacement" },
    { icon: FileCheck, title: "Insurance Claim Assistance", desc: "Help with insurance paperwork" },
    { icon: CheckCircle, title: "All Car Models Supported", desc: "Service for every make and model" }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-12 h-1 bg-[#0B3D91]"></div>
            <span className="text-[#0B3D91] font-semibold text-sm uppercase tracking-wider">
              Our Services
            </span>
            <div className="w-12 h-1 bg-[#0B3D91]"></div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#111111]">
            What We <span className="text-[#0B3D91]">Offer</span>
          </h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="service-card bg-white p-6 rounded-xl border border-gray-100 cursor-pointer"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-[#0B3D91]/10 rounded-xl flex items-center justify-center mb-4 icon-container">
                <service.icon className="w-7 h-7 text-[#0B3D91]" />
              </div>
              <h3 className="font-bold text-[#111111] mb-2">{service.title}</h3>
              <p className="text-gray-600 text-sm">{service.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const highlights = [
    { number: "15+", title: "Years Experience", desc: "Trusted expertise since 2009" },
    { number: "5000+", title: "Happy Customers", desc: "Satisfied car owners" },
    { number: "24hr", title: "Fast Service", desc: "Same day service available" },
    { number: "100%", title: "Quality Assured", desc: "Genuine parts guaranteed" }
  ];

  const features = [
    "Expert Technicians",
    "Affordable Pricing",
    "Fast Service",
    "Trusted by Customers",
    "Same Day Service Available",
    "Insurance Claim Support"
  ];

  return (
    <section data-testid="why-choose-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-12 h-1 bg-[#E53935]"></div>
              <span className="text-[#E53935] font-semibold text-sm uppercase tracking-wider">
                Why Choose Us
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-[#111111] mb-8">
              The Best Choice for{" "}
              <span className="text-[#0B3D91]">Your Car</span>
            </h2>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F8F9FA] p-5 rounded-xl highlight-card"
                >
                  <p className="text-3xl font-extrabold text-[#0B3D91] mb-1">{item.number}</p>
                  <p className="font-semibold text-[#111111] text-sm">{item.title}</p>
                  <p className="text-gray-500 text-xs mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col justify-center"
          >
            <div className="bg-[#0B3D91] rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-6">Our Commitment to You</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#E53935] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-100">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-white/20">
                <a
                  href="tel:9511323693"
                  className="inline-flex items-center gap-2 bg-white text-[#0B3D91] px-6 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors"
                  data-testid="why-choose-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Call: 9511323693
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const images = [
    {
      src: "https://images.pexels.com/photos/11950154/pexels-photo-11950154.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      alt: "Windshield repair process",
      span: "col-span-2"
    },
    {
      src: "https://images.unsplash.com/photo-1741906060079-3edebafd5300?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Car side window detail",
      span: "col-span-1"
    },
    {
      src: "https://images.unsplash.com/photo-1767681092416-bccf9410bda4?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Workshop overview",
      span: "col-span-1"
    },
    {
      src: "https://images.pexels.com/photos/11950157/pexels-photo-11950157.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      alt: "Technician applying sealant",
      span: "col-span-1"
    },
    {
      src: "https://images.unsplash.com/photo-1771340742493-52fbd5476ccb?crop=entropy&cs=srgb&fm=jpg&q=85",
      alt: "Mechanic at work",
      span: "col-span-1"
    }
  ];

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-12 h-1 bg-[#0B3D91]"></div>
            <span className="text-[#0B3D91] font-semibold text-sm uppercase tracking-wider">
              Our Work
            </span>
            <div className="w-12 h-1 bg-[#0B3D91]"></div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#111111]">
            Gallery
          </h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {images.map((image, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className={`${index === 0 ? "lg:col-span-2" : ""} overflow-hidden rounded-xl`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="gallery-image w-full h-48 sm:h-64 lg:h-72 object-cover cursor-pointer"
                data-testid={`gallery-image-${index}`}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We will contact you soon.");
      setFormData({ name: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-12 h-1 bg-[#0B3D91]"></div>
              <span className="text-[#0B3D91] font-semibold text-sm uppercase tracking-wider">
                Contact Us
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-[#111111] mb-6">
              Get In <span className="text-[#0B3D91]">Touch</span>
            </h2>

            <p className="text-gray-600 mb-8">
              Have a question or need a quote? Fill out the form below and we'll get back to you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#111111] mb-2">
                  Your Name
                </label>
                <Input
                  type="text"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input h-12 border-gray-200 focus:border-[#0B3D91] focus:ring-[#0B3D91]"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111111] mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input h-12 border-gray-200 focus:border-[#0B3D91] focus:ring-[#0B3D91]"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111111] mb-2">
                  Message
                </label>
                <Textarea
                  placeholder="How can we help you?"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="form-input border-gray-200 focus:border-[#0B3D91] focus:ring-[#0B3D91]"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-[#E53935] hover:bg-[#C62828] text-white font-semibold btn-accent"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col"
          >
            <div className="bg-[#111111] rounded-2xl p-8 text-white flex-grow">
              <h3 className="text-xl font-bold mb-8">Contact Information</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0B3D91] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Business Name</p>
                    <p className="font-semibold">Car Glass & Body Parts Specialist</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0B3D91] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Location</p>
                    <p className="font-semibold">Nirman Nagar, Jaipur, Rajasthan</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#E53935] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Phone</p>
                    <a href="tel:9511323693" className="font-semibold text-lg hover:text-[#E53935] transition-colors">
                      9511323693
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0B3D91] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Working Hours</p>
                    <p className="font-semibold">Open Daily: 9:00 AM – 11:00 PM</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="flex gap-4">
                  <a
                    href="tel:9511323693"
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B3D91] text-white px-4 py-3 rounded-md font-semibold hover:bg-[#083070] transition-colors"
                    data-testid="contact-call-btn"
                  >
                    <Phone className="w-5 h-5" />
                    Call Now
                  </a>
                  <a
                    href="https://wa.me/919511323693"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-3 rounded-md font-semibold hover:bg-[#128C7E] transition-colors"
                    data-testid="contact-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#111111] text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-[#0B3D91] rounded-lg flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold">Car Glass & Body Parts</h3>
                <p className="text-xs text-gray-400">Specialist</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your trusted partner for car glass repair and body parts solutions in Jaipur.
            </p>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <MapPin className="w-4 h-4" />
              <span>Nirman Nagar, Jaipur</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {["Home", "Services", "Contact"].map((link) => (
                <li key={link}>
                  <a
                    href={`#${link.toLowerCase()}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(`#${link.toLowerCase()}`)?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold mb-4">Contact</h4>
            <div className="space-y-3">
              <a
                href="tel:9511323693"
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
              >
                <Phone className="w-4 h-4" />
                9511323693
              </a>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Clock className="w-4 h-4" />
                9:00 AM – 11:00 PM
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <p className="text-center text-gray-500 text-sm">
            © {currentYear} Car Glass & Body Parts Specialist. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating Buttons Component
const FloatingButtons = () => {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
      {/* WhatsApp Button */}
      <a
        href="https://wa.me/919511323693"
        target="_blank"
        rel="noopener noreferrer"
        className="floating-btn whatsapp-btn w-14 h-14 rounded-full flex items-center justify-center shadow-lg pulse-glow"
        data-testid="floating-whatsapp-btn"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>

      {/* Phone Button */}
      <a
        href="tel:9511323693"
        className="floating-btn phone-btn w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
        data-testid="floating-phone-btn"
        aria-label="Call Now"
      >
        <Phone className="w-7 h-7 text-white" />
      </a>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <WhyChooseUsSection />
        <GallerySection />
        <ContactSection />
      </main>
      <Footer />
      <FloatingButtons />
    </div>
  );
}

export default App;
